import { Injectable } from '@angular/core';
import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { FormGroup } from '@angular/forms';
import { INVALID_LAST_NAMES_CONSTANTS, INVALID_NAMES_CONSTANTS } from '@app/shared/constants/invalid-string.constants';

@Injectable({ providedIn: 'root' })
export class ValidatorsService {
  public firstNameAndLastnamePattern: string = '([a-zA-Z]+) ([a-zA-Z]+)';
  public emailPattern: string = '^[a-z0-9._%+-]+@[a-z0-9.-]+\\.[a-z]{2,4}$';

  public isValidField(form: FormGroup, field: string) {
    const control = form.get(field);
    return control && control.errors && (control.dirty || control.touched);
  }

  public validateNumber(event: KeyboardEvent): void {
    const pattern = /[0-9]/;
    const inputChar = String.fromCharCode(event.charCode);

    if (!pattern.test(inputChar)) {
      // invalid character, prevent input
      event.preventDefault();
    }
  }
}

export function numericValidator(): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } | null => {
    const valid = /^\d+$/.test(control.value);
    return valid ? null : { 'numeric': { value: control.value } };
  };
}

export function isValidField(form: FormGroup, field: string) {
  const control = form.get(field);
  if (!control) {
    return false;
  }
  return control.errors && (control.dirty || control.touched);
}


export function correoConDosExtensiones(control: AbstractControl): ValidationErrors | null {
  const rawValue = control.value;

  if (!rawValue) return null;

  const valor = String(rawValue).trim();

  if (valor !== rawValue) {
    control.setValue(valor, { emitEvent: false });
  }

  const regex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9-]+(\.[a-zA-Z]{2,}){1,2}$/;
  const match = valor.match(regex);

  if (!match) {
    return { formatoInvalido: true };
  }

  // Verificamos que tenga solo una o dos extensiones
  const dominioPartes = valor.split('@')[1].split('.');
  if (dominioPartes.length > 3) { // dominio + 2 extensiones = 3 partes
    return { demasiadasExtensiones: true };
  }

  return null;
}



// ─────────────────────────────────────────────────────────────────────────────
// Nuevos validadores
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Valida dirección postal:
 * - Caracteres permitidos: letras (A-Z, a-z, Ñ/ñ), números, espacios,
 *   punto (.), guion medio (-), numeral (#), asterisco (*),
 *   barra diagonal (/) y barra invertida (\).
 * - Longitud mínima: 5 caracteres. Máxima: 150 caracteres.
 */
export function ValidateAddress(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const valor: string = control.value ?? '';

    if (!valor) return null;

    if (valor.length < 5) {
      return { direccion: 'La dirección debe tener al menos 5 caracteres' };
    }

    if (valor.length > 150) {
      return { direccion: 'La dirección no puede superar los 150 caracteres' };
    }

    const caracteresPermitidos = /^[A-Za-zÑñ0-9 .#\-*\\/]+$/;
    if (!caracteresPermitidos.test(valor)) {
      return {
        direccion: 'La dirección solo puede contener letras, números, espacios y los caracteres: . - # * / \\',
      };
    }

    return null;
  };
}

/**
 * Valida correo electrónico con reglas de calidad estrictas:
 * - Elimina espacios automáticamente antes de validar.
 * - Nombre de usuario: letras, números, punto, guion, guion bajo;
 *   el carácter justo antes del @ debe ser letra o número.
 * - No se permite punto (.) inmediatamente después del @.
 * - No se permite .com, .gov.co o .co directamente tras el @ sin servidor.
 * - Servidor: letras, números y guion medio.
 * - TLD: 2-3 letras o el dominio especial "aero"/"AERO".
 * - Subdominio compuesto opcional (ej.: .com.co).
 */
export function ValidateEmail(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const rawValue = control.value;

    if (!rawValue) {
      return null;
    }

    const value = String(rawValue).trim();

    if (value !== rawValue) {
      control.setValue(value, { emitEvent: false });
    }

    // Valida la estructura general
    const emailRegex =
      /^[A-Za-z0-9._-]+[A-Za-z0-9]@(?!\.)(?!\.com\b)(?!\.gov\.co\b)(?!\.co\b)[A-Za-z0-9][A-Za-z0-9-]*(?:\.[A-Za-z0-9-]+)+$/i;

    if (!emailRegex.test(value)) {
      return {
        emailInvalido: 'Asegúrate de ingresar un correo válido',
      };
    }

    const [, domain] = value.split('@');
    const parts = domain.split('.');

    // Máximo 4 segmentos:
    // servidor.subdominio.tld
    // servidor.tld
    // servidor.tld.tld
    // servidor.subdominio.tld.tld
    if (parts.length > 4) {
      return {
        emailInvalido: 'Asegúrate de ingresar un correo válido',
      };
    }

    // Los últimos 1, 2 o 3 segmentos deben ser únicamente letras
    const tlds = parts.slice(1);

    if (!tlds.every(part => /^[A-Za-z]{2,}$/.test(part))) {
      return {
        emailInvalido: 'Asegúrate de ingresar un correo válido',
      };
    }

    return null;
  };
}

/**
 * Valida número de celular colombiano:
 * - Elimina espacios automáticamente antes de validar.
 * - Solo dígitos, exactamente 10.
 * - Indicativo válido en rango 300–350:
 *   /^3(0[0-7]|1\d|2[0-4]|50)\d{7}$/
 */
export function ValidateColombianPhone(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) return null;

    const valor: string = (control.value as string).replace(/\s+/g, '');

    if (valor !== control.value) {
      control.setValue(valor, { emitEvent: false });
    }

    if (!/^\d+$/.test(valor)) {
      return { celular: 'El número de celular debe contener solo dígitos' };
    }

    if (valor.length !== 10) {
      return { celular: 'El número de celular debe tener exactamente 10 dígitos' };
    }

    const celularRegex = /^3(0[0-7]|1\d|2[0-4]|50)\d{7}$/;
    return celularRegex.test(valor)
      ? null
      : { celular: 'Ingresa un número de celular colombiano válido (ej.: 3001234567)' };
  };
}


/**
 * Valida que el campo de nombres/apellidos no contenga palabras,
 * frases o patrones considerados "basura" (datos de prueba, placeholders,
 * texto errado, secuencias de caracteres repetidos, etc.).
 *
 * Reglas:
 * - Compara el valor completo (normalizado a mayúsculas, espacios colapsados)
 *   contra una lista de palabras/frases prohibidas.
 * - Rechaza secuencias compuestas únicamente por guiones (---, ------, etc.).
 * - Rechaza valores formados por un mismo carácter repetido 3 o más veces
 *   (AAA, XXXX, BBBBBBB, SSSS, etc.).
 */
export function ValidateForbiddenName(): ValidatorFn {
  const INVALID_NAMES: string[] = INVALID_NAMES_CONSTANTS; 

  return (control: AbstractControl): ValidationErrors | null => {
    const rawValue = control.value;

    
    if (!rawValue) return null;

    const valor = String(rawValue).trim();
    const valorNormalizado = valor.toUpperCase().replace(/\s+/g, ' ');

    // Coincidencia exacta contra la lista de palabras/frases prohibidas
    if (INVALID_NAMES.includes(valorNormalizado)) {
      return { nombreInvalido: 'Asegúrate de ingresar un nombre válido' };
    }

    // Coincidencia de palabras prohibidas dentro del texto
    const palabrasRegex = new RegExp(`\\b(${INVALID_NAMES.map(p => p.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})\\b`, 'i');
    if (palabrasRegex.test(valor)) {
      return { nombreInvalido: 'Asegúrate de ingresar un nombre válido' };
    }

    // Secuencias compuestas solo por guiones: ---, ------, --------------------
    if (/\-{3,}/.test(valor)) {
      return { nombreInvalido: 'Asegúrate de ingresar un nombre válido' };
    }

    // Un mismo carácter repetido 3 o más veces en cualquier parte del texto: AAA, XXXX, BBBBBBB, SSSS, etc.
    if (/([A-Za-zÑñ])\1{2,}/.test(valor)) {
      return { nombreInvalido: 'Asegúrate de ingresar un nombre válido' };
    }

    return null;
  };
}


export function ValidateForbiddenLastNames(): ValidatorFn {
  const INVALID_LAST_NAMES: string[] = INVALID_LAST_NAMES_CONSTANTS;

  return (control: AbstractControl): ValidationErrors | null => {
    const rawValue = control.value;

    
    if (!rawValue) return null;

    const valor = String(rawValue).trim();
    const valorNormalizado = valor.toUpperCase().replace(/\s+/g, ' ');

    // Coincidencia exacta contra la lista de palabras/frases prohibidas
    if (INVALID_LAST_NAMES.includes(valorNormalizado)) {
      return { lastNameInvalido: 'Asegúrate de ingresar un apellido válido' };
    }

    // Coincidencia de palabras prohibidas dentro del texto
    const palabrasRegex = new RegExp(`\\b(${INVALID_LAST_NAMES.map(p => p.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})\\b`, 'i');
    if (palabrasRegex.test(valor)) {
      return { lastNameInvalido: 'Asegúrate de ingresar un apellido válido' };
    }

    // Secuencias compuestas solo por guiones: ---, ------, --------------------
    if (/\-{3,}/.test(valor)) {
      return { lastNameInvalido: 'Asegúrate de ingresar un apellido válido' };
    }

    // Un mismo carácter repetido 3 o más veces en cualquier parte del texto: AAA, XXXX, BBBBBBB, SSSS, etc.
    if (/([A-Za-zÑñ])\1{2,}/.test(valor)) {
      return { lastNameInvalido: 'Asegúrate de ingresar un apellido válido' };
    }

    return null;
  };
}